// NEW_FILE_JUSTIFIED: WhatsApp connection card for Connected Accounts page — QR code pairing flow
import { cn } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Phone, CheckCircle, Loader2, QrCode } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useWhatsAppConnect } from '@/hooks/useWhatsAppConnect';

const statusMessages: Record<string, string | null> = {
  connecting: 'Starting WhatsApp connection...',
  awaiting_qr: 'QR code ready. Open WhatsApp on your phone and scan the code.',
  expired: 'QR code expired. Please try again.',
};

export function WhatsAppConnect() {
  const { session, step, errorMessage, qrCode, isConnected, handleConnect, handleDisconnect } =
    useWhatsAppConnect();

  const statusMessage = isConnected ? null : (statusMessages[step] ?? null);

  return (
    <section
      aria-labelledby="whatsapp-connect-heading"
      className="flex flex-col gap-4 rounded-xl border border-border bg-card p-5"
    >
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
          <Phone size={18} className="text-muted-foreground" aria-hidden="true" />
        </div>
        <div>
          <h3 id="whatsapp-connect-heading" className="text-sm font-semibold text-foreground">
            WhatsApp
          </h3>
          <p className="text-xs text-muted-foreground">
            {isConnected && session?.phoneNumber
              ? `+${session.phoneNumber}`
              : 'Connect your WhatsApp to send and receive messages'}
          </p>
        </div>

        {isConnected && (
          <div className="ml-auto flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400">
            <CheckCircle size={13} aria-hidden="true" />
            <span>Connected</span>
          </div>
        )}
      </div>

      <Separator />

      {/* Error — always-mounted assertive live region */}
      <p
        role="alert"
        aria-atomic="true"
        className={cn(
          'rounded-lg px-3 py-2 text-xs text-destructive',
          errorMessage ? 'bg-destructive/10' : 'sr-only'
        )}
      >
        {errorMessage ?? ''}
      </p>

      {/* Status — always-mounted polite live region for step transitions */}
      <p
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className={cn('text-sm text-muted-foreground', statusMessage ? '' : 'sr-only')}
      >
        {statusMessage ?? ''}
      </p>

      {/* State: Connected */}
      {isConnected && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Connected as +{session?.phoneNumber ?? 'your WhatsApp account'}
          </p>
          <Button variant="outline" size="sm" onClick={handleDisconnect}>
            Disconnect
          </Button>
        </div>
      )}

      {/* State: QR Code display */}
      {step === 'awaiting_qr' && qrCode && !isConnected && (
        <div className="flex flex-col items-center gap-3 py-2">
          <QRCodeSVG
            value={qrCode}
            size={200}
            level="M"
            title="WhatsApp QR code — scan with your phone to link this account"
          />
          <p className="text-xs text-muted-foreground">
            Open WhatsApp on your phone → Settings → Linked Devices → Scan this QR code
          </p>
        </div>
      )}

      {/* State: Expired */}
      {step === 'expired' && !isConnected && (
        <div className="flex flex-col items-center gap-3 py-2">
          <QrCode size={40} className="text-muted-foreground/40" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">QR code expired</p>
          <Button
            variant="outline"
            size="sm"
            onClick={handleConnect}
            aria-label="Try connecting WhatsApp again"
          >
            Try again
          </Button>
        </div>
      )}

      {/* State: Disconnected — show connect button */}
      {!isConnected && step === 'disconnected' && (
        <Button onClick={handleConnect} className="w-full">
          <Phone size={16} className="mr-2" aria-hidden="true" />
          Connect WhatsApp Account
        </Button>
      )}

      {/* State: Loading / Connecting (before QR arrives) */}
      {step === 'connecting' && !qrCode && !isConnected && (
        <div className="flex items-center justify-center gap-2 py-3">
          <Loader2 size={16} className="animate-spin text-muted-foreground" aria-hidden="true" />
          <span className="text-sm text-muted-foreground">Starting WhatsApp connection...</span>
        </div>
      )}
    </section>
  );
}
